import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { baseUrl } from "components/shared/apiCrudBuilder";
import { AuthenticationContext } from "components/context/AuthenticationProvider";
import { useContext } from "react";
import { SnackbarContext } from "components/context/SnackbarProvider";
import { retryOnError } from "components/shared/apiCrudBuilder";

const path = `${baseUrl}/sourcing`;
const keyName = "Sourcing";

export const useAllSourcing = (params) => {
  const { appAxios } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [`all_${keyName}`, params],
    queryFn: () => {
      return appAxios.get(`${path}/`, { params: params }).then((res) => {
        const storeInCache = (item) => {
          queryClient.setQueryData([keyName, `${item.id}`], item, {
            staleTime: 10000,
            cacheTime: 10000,
          });
        };
        if (params?.display_mode === "COMMITTEE") {
          res.data.results["Committee"].forEach(storeInCache);
          res.data.results["Try reach"].forEach(storeInCache);
          res.data.results["Reminder"].forEach(storeInCache);
          res.data.results["Closed (In scope)"].forEach(storeInCache);
          res.data.results["Closed (Out of scope)"].forEach(storeInCache);
        } else {
          res.data.results.forEach(storeInCache);
        }

        return res.data;
      });
    },
    enabled: params !== null,
    retry: retryOnError,
  });
};

export const useSourcing = (id, options) => {
  const { appAxios } = useContext(AuthenticationContext);

  return useQuery({
    staleTime: 10000,
    cacheTime: 10000,
    ...options,
    queryKey: [keyName, `${id}`],
    queryFn: () => appAxios.get(`${path}/${id}/`).then((res) => res.data),
    retry: retryOnError,
  });
};

export const useCreateSourcing = (options) => {
  const queryClient = useQueryClient();
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: (newObject) =>
      appAxios.post(`${path}/`, newObject).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      queryClient.invalidateQueries({ queryKey: ["CompanyDealAndSourcing"] });
    },
    onError: displayError,
  });
};

export const useUpdateSourcing = (id, options) => {
  const queryClient = useQueryClient();
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: (updates) =>
      appAxios.put(`${path}/${id}/`, updates).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyName, `${id}`] });
      queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      queryClient.invalidateQueries({ queryKey: ["CompanyDealAndSourcing"] });
    },
    onError: displayError,
  });
};

export const useDeleteSourcing = (id, options) => {
  const queryClient = useQueryClient();
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: () => appAxios.delete(`${path}/${id}/`).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyName, id] });
      queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      queryClient.invalidateQueries({ queryKey: ["CompanyDealAndSourcing"] });
    },
    onError: displayError,
  });
};

export const useSourcingHistory = (sourcingId, options) => {
  const { appAxios } = useContext(AuthenticationContext);

  return useQuery({
    ...options,
    queryKey: ["Sourcing", sourcingId, "sourcingHistory"],
    queryFn: () =>
      appAxios.get(`${path}/${sourcingId}/history/`).then((res) => res.data),
    retryOnError: retryOnError,
  });
};

export const useValidateCommitteeMeeting = (options) => {
  const queryClient = useQueryClient();
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: (dealFlowId) =>
      appAxios
        .post(`${path}/validate_committee_meeting/`, null, {
          params: {
            deal_flow: dealFlowId,
          },
        })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`all_Sourcing`] });
    },
    onError: displayError,
  });
};

export const useCommitteeMeeting = (dealFlowId, options) => {
  const { appAxios } = useContext(AuthenticationContext);

  return useQuery({
    ...options,
    queryKey: ["committeeMeeting", dealFlowId],
    queryFn: () =>
      appAxios
        .get(`${path}/committee_meeting/`, {
          params: {
            deal_flow: dealFlowId,
          },
        })
        .then((res) => res.data),
  });
};
